import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Sparkles, CreditCard, Crown, Clock } from "lucide-react";

export default function Dashboard() {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/products")
            .then(({ data }) => setProducts(data))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-6 py-12" data-testid="dashboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
                <div>
                    <div className="overline mb-2">Dashboard</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
                        Hey {user?.name?.split(" ")[0] || "there"}<span className="text-accent">.</span>
                    </h1>
                    <p className="text-zinc-500 mt-2">Let&apos;s make a listing.</p>
                </div>
                <Link to="/generate" className="btn-primary" data-testid="dashboard-new">
                    <Plus className="w-4 h-4" /> New listing
                </Link>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                <div className="surface p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="overline">Credits</span>
                        <CreditCard className="w-4 h-4 text-zinc-600" />
                    </div>
                    <div className="font-heading font-bold text-4xl tracking-tighter text-zinc-100">{user?.credits ?? 0}</div>
                    <Link to="/pricing" className="text-xs text-accent hover:underline mt-2 inline-block" data-testid="dashboard-buy-credits">Buy more →</Link>
                </div>
                <div className="surface p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="overline">Listings created</span>
                        <Sparkles className="w-4 h-4 text-zinc-600" />
                    </div>
                    <div className="font-heading font-bold text-4xl tracking-tighter text-zinc-100">{products.length}</div>
                    <span className="text-xs text-zinc-500 mt-2 inline-block">All-time</span>
                </div>
                <div className="surface p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="overline">Plan</span>
                        <Crown className={`w-4 h-4 ${user?.is_premium ? "text-accent" : "text-zinc-600"}`} />
                    </div>
                    <div className="font-heading font-bold text-3xl tracking-tighter text-zinc-100">
                        {user?.is_premium ? "Premium" : "Free"}
                    </div>
                    {user?.is_premium ? (
                        <div className="text-xs text-accent mt-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Priority queue</div>
                    ) : (
                        <Link to="/pricing" className="text-xs text-accent hover:underline mt-2 inline-block" data-testid="dashboard-upgrade">Upgrade →</Link>
                    )}
                </div>
            </div>

            {/* History */}
            <div>
                <div className="flex items-baseline justify-between mb-6">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight">Recent listings</h2>
                    <span className="overline">{products.length} total</span>
                </div>

                {/* Recent listings — early return pattern instead of nested ternary */}
                {(() => {
                    if (loading) {
                        return <div className="surface p-12 text-center font-mono text-sm text-zinc-500" data-testid="dashboard-loading">Loading...</div>;
                    }
                    if (products.length === 0) {
                        return (
                            <div className="surface p-12 text-center" data-testid="dashboard-empty">
                                <div className="font-heading text-2xl font-semibold tracking-tight mb-2">No listings yet.</div>
                                <p className="text-zinc-500 mb-6">Create your first AI-powered listing in under a minute.</p>
                                <Link to="/generate" className="btn-primary inline-flex" data-testid="dashboard-empty-cta">
                                    <Plus className="w-4 h-4" /> Create your first listing
                                </Link>
                            </div>
                        );
                    }
                    return (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-products">
                            {products.map((p) => (
                                <Link key={p.id} to={`/results/${p.id}`} className="surface surface-hover p-4 group" data-testid={`product-card-${p.id}`}>
                                    {p.thumbnail && (
                                        <div className="aspect-square rounded-md bg-surface-2 border border-white/5 mb-3 overflow-hidden">
                                            <img src={`data:image/jpeg;base64,${p.thumbnail}`} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="overline mb-1">{p.marketplace}</div>
                                    <h3 className="font-medium text-zinc-100 line-clamp-2 mb-1">{p.title}</h3>
                                    <div className="text-xs font-mono text-zinc-600">{new Date(p.created_at).toLocaleDateString()}</div>
                                </Link>
                            ))}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}
